import React, { useEffect, useState } from "react";
import { ArrowLeftRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, Empty, ResBadge, RowSearch, useQueryFilter, matchesQuery } from "@/pages/shared";

const dayBefore = (iso) => new Date(new Date(`${iso}T00:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10);

// De verhuisdatum moet binnen het verblijf vallen
const clampDate = (iso, r) => {
  const last = dayBefore(r.departure);
  return iso < r.arrival ? r.arrival : iso > last ? last : iso;
};

export default function Verhuizen() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [moves, setMoves] = useState([]);
  const [zones, setZones] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [free, setFree] = useState([]);
  const [sel, setSel] = useState(null);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [target, setTarget] = useState({ zone: "", pitch_label: "", move_date: "", reason: "" });
  const [q, setQ] = useQueryFilter();

  const load = async () => {
    try {
      const [r, z, p, m] = await Promise.all([api.get("/reservations?filter=staying"), api.get("/zones"), api.get("/pitches"), api.get("/moves?status=all")]);
      setRows(r.data);
      setZones(z.data);
      setPitches(p.data);
      setMoves(m.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const executeMove = async (id) => {
    try {
      await api.post(`/moves/${id}/execute`);
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Enkel plaatsen die de volledige resterende periode vrij blijven
  useEffect(() => {
    if (!sel || !target.move_date) { setFree([]); return; }
    const kind = pitches.find((p) => p.label === sel.pitch_label)?.kind;
    api.get(`/pitches/free?start=${target.move_date}&end=${sel.departure}&exclude_reservation=${sel.id}${kind ? `&kind=${kind}` : ""}`)
      .then((r) => setFree(r.data.filter((p) => p.label !== sel.pitch_label)))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
    // enkel herrekenen bij een andere selectie of verhuisdatum
  }, [sel, target.move_date, pitches]);

  const doMove = async () => {
    if (!target.pitch_label) { toast.error(t("move_pick_free")); return; }
    try {
      await api.post(`/reservations/${sel.id}/move`, { ...target, reason: (target.reason || "").trim() || t("move_reason_default") });
      setSel(null);
      setTarget({ zone: "", pitch_label: "", move_date: "", reason: "" });
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const zoneName = (code) => zones.find((z) => z.code === code)?.name || code;
  const zoneColor = (code) => zones.find((z) => z.code === code)?.color || undefined;
  const pending = moves.filter((m) => m.status === "pending");
  const done = moves.filter((m) => m.status === "done");
  const freePitches = free.filter((p) => zoneFilter === "all" || p.zone === zoneFilter);
  const visible = rows.filter((r) => matchesQuery(r, q));

  return (
    <Panel title={`${t("nav_verhuizen")} (${visible.length}${q ? `/${rows.length}` : ""})`} testid="move-panel"
      action={<RowSearch value={q} onChange={setQ} placeholder={t("guests_search")} testid="move-search" />}>
      {(pending.length > 0 || done.length > 0) && (
        <div className="space-y-4 mb-5" data-testid="scheduled-moves">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-1.5">{t("move_pending")} · {pending.length}</p>
            {pending.length === 0 ? <p className="text-xs text-muted-foreground/70">—</p> : (
              <div className="space-y-1.5">
                {pending.map((m) => (
                  <div key={m.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${m.due ? "border-amber-400 bg-amber-50" : "border-border"}`} data-testid={`pending-move-${m.id}`}>
                    <span className="text-sm font-medium flex-1 truncate">{m.guest_name}</span>
                    <span className="flex items-center gap-1 text-sm"><span className="font-semibold" style={{ color: zoneColor(m.zone_from) }}>{m.from_pitch}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-semibold" style={{ color: zoneColor(m.zone_to) }}>{m.to_pitch}</span>{m.zone_from !== m.zone_to && <span className="text-[10px] font-semibold uppercase tracking-wide ml-1" style={{ color: zoneColor(m.zone_to) }}>{t("move_other_zone")}</span>}</span>
                    <span className="text-xs text-muted-foreground w-28 text-right">{m.date}{m.due ? ` · ${t("move_due")}` : ""}</span>
                    <Button size="sm" onClick={() => executeMove(m.id)} data-testid={`execute-move-${m.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />{t("move_execute")}</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {done.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{t("move_done")} · {done.length}</p>
              <div className="space-y-1.5">
                {done.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 opacity-70" data-testid={`done-move-${m.id}`}>
                    <span className="text-sm font-medium flex-1 truncate">{m.guest_name}</span>
                    <span className="flex items-center gap-1 text-sm"><span className="font-semibold" style={{ color: zoneColor(m.zone_from) }}>{m.from_pitch}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-semibold" style={{ color: zoneColor(m.zone_to) }}>{m.to_pitch}</span>{m.zone_from !== m.zone_to && <span className="text-[10px] font-semibold uppercase tracking-wide ml-1" style={{ color: zoneColor(m.zone_to) }}>{t("move_other_zone")}</span>}</span>
                    <span className="text-xs text-muted-foreground w-28 text-right">{m.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {visible.length === 0 ? <Empty /> : (
        <div className="max-h-[70vh] overflow-auto cb-scroll overscroll-contain" data-testid="move-scroll">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("zone")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} data-testid={`move-row-${r.id}`}>
                <TableCell className="font-medium">{r.guest_name}</TableCell>
                <TableCell><span className="font-semibold text-primary">{r.pitch_label}</span></TableCell>
                <TableCell>{r.zone}</TableCell>
                <TableCell><ResBadge status={r.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => { setSel(r); setZoneFilter("all"); setTarget({ zone: "", pitch_label: "", move_date: clampDate(new Date().toISOString().slice(0, 10), r), reason: t("move_reason_default") }); }} data-testid={`move-${r.id}`}>
                    <ArrowLeftRight className="h-4 w-4 mr-1" />{t("move")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("move")} — {sel?.guest_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("move_from")}</Label>
              <div className="h-10 flex items-center rounded-md border border-border bg-muted/60 px-3" data-testid="move-from">
                <span className="font-display font-bold text-base text-foreground">{sel?.pitch_label}</span>
                <span className="ml-2 text-xs text-muted-foreground">{sel?.zone}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("move_to")}</Label>
              <Select value={target.pitch_label} onValueChange={(v) => {
                const p = free.find((x) => x.label === v);
                setTarget({ ...target, pitch_label: v, zone: p?.zone || "" });
              }}>
                <SelectTrigger data-testid="move-pitch"><SelectValue placeholder={t("move_pick_free")} /></SelectTrigger>
                <SelectContent>
                  {freePitches.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">{t("move_none_free")}</div>}
                  {freePitches.map((p) => (
                    <SelectItem key={p.label} value={p.label} data-testid={`move-option-${p.label}`}>
                      {p.label} — {zoneName(p.zone)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("zone")}</Label>
            <Select value={zoneFilter} onValueChange={setZoneFilter}>
              <SelectTrigger data-testid="move-zone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("move_all_zones")}</SelectItem>
                {zones.map((z) => <SelectItem key={z.code} value={z.code}>{z.code} — {z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 mt-3">
            <Label className="text-xs">{t("move_date")}</Label>
            <Input type="date" value={target.move_date} min={sel?.arrival}
              max={sel && dayBefore(sel.departure)}
              onChange={(e) => setTarget({ ...target, move_date: e.target.value })} data-testid="move-date" />
            <p className="text-[11px] text-muted-foreground">{t("split_move_desc")}</p>
            <p className="text-[11px] text-muted-foreground" data-testid="move-free-note">{t("move_free_whole_stay")}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("move_reason")}</Label>
            <Input value={target.reason} onChange={(e) => setTarget({ ...target, reason: e.target.value })}
              placeholder={t("move_reason_default")} data-testid="move-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)}>{t("cancel")}</Button>
            <Button onClick={doMove} data-testid="save-move-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
